import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";

function Section({ id, title, children }) {
  return (
    <Box id={id} sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Body({ children, sx }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.8, ...sx }}>
      {children}
    </Typography>
  );
}

function Bullet({ children }) {
  return (
    <Typography
      component="li"
      variant="body2"
      color="text.secondary"
      sx={{ mb: 0.75, lineHeight: 1.8, ml: 2 }}
    >
      {children}
    </Typography>
  );
}

export default function PrivacyPolicy() {
  return (
    <Box
      sx={{
        maxWidth: 760,
        mx: "auto",
        px: { xs: 3, sm: 5 },
        py: { xs: 5, sm: 7 },
      }}
    >
      {/* Header */}
      <Typography variant="h4" fontWeight={800} letterSpacing={-0.5} sx={{ mb: 0.5 }}>
        Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Last updated May 31, 2026
      </Typography>

      <Body>
        This Privacy Notice for <strong>BlinkzUS</strong> ("we," "us," or "our") describes
        how and why we might access, collect, store, use, and/or share ("process") your
        personal information when you use our services ("Services"), including when you visit
        our website at{" "}
        <Link href="https://blinkz.us" color="primary">blinkz.us</Link> or use the Blinkz
        URL shortener with QR generation and advanced link management tools.
      </Body>
      <Body>
        <strong>Questions or concerns?</strong> If you do not agree with our policies,
        please do not use our Services. Contact us at{" "}
        <Link href="mailto:imintcomx@gmail.com" color="primary">imintcomx@gmail.com</Link>.
      </Body>

      <Divider sx={{ my: 4 }} />

      {/* TOC */}
      <Section id="toc" title="Table of Contents">
        {[
          ["#infocollect",   "1. What information do we collect?"],
          ["#infouse",       "2. How do we process your information?"],
          ["#legalbases",    "3. What legal bases do we rely on?"],
          ["#whoshare",      "4. When and with whom do we share your information?"],
          ["#cookies",       "5. Do we use cookies and tracking technologies?"],
          ["#sociallogins",  "6. How do we handle your social logins?"],
          ["#inforetain",    "7. How long do we keep your information?"],
          ["#infosafe",      "8. How do we keep your information safe?"],
          ["#infominors",    "9. Do we collect information from minors?"],
          ["#privacyrights", "10. What are your privacy rights?"],
          ["#DNT",           "11. Controls for Do-Not-Track features"],
          ["#uslaws",        "12. Do United States residents have specific privacy rights?"],
          ["#policyupdates", "13. Do we make updates to this notice?"],
          ["#contact",       "14. How can you contact us about this notice?"],
          ["#request",       "15. How can you review, update, or delete the data we collect?"],
        ].map(([href, label]) => (
          <Typography key={href} variant="body2" sx={{ mb: 0.5 }}>
            <Link href={href} color="primary" underline="hover">{label}</Link>
          </Typography>
        ))}
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* 1 */}
      <Section id="infocollect" title="1. What Information Do We Collect?">
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Personal information you disclose to us
        </Typography>
        <Body>
          We collect personal information that you voluntarily provide when you register on
          the Services, express an interest in our products, or otherwise contact us. This
          includes:
        </Body>
        <ul>
          <Bullet>Email addresses (via Google Sign-In)</Bullet>
        </ul>
        <Body sx={{ mt: 1.5 }}>
          <strong>Sensitive information.</strong> We do not process sensitive information.
        </Body>
        <Body>
          <strong>Payment data.</strong> We may collect data necessary to process your
          payment. All payment data is handled and stored by{" "}
          <Link href="https://stripe.com/privacy" target="_blank" color="primary">Stripe</Link>.
          We do not store your card details.
        </Body>
        <Body>
          <strong>Social media login data.</strong> We offer registration via Google. We
          receive your name, email address, and profile picture from Google. See section 6
          for more details.
        </Body>
        <Body>
          <strong>Google API.</strong> Our use of information received from Google APIs
          adheres to the{" "}
          <Link href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" color="primary">
            Google API Services User Data Policy
          </Link>
          , including the Limited Use requirements.
        </Body>
      </Section>

      {/* 2 */}
      <Section id="infouse" title="2. How Do We Process Your Information?">
        <Body>
          We process your information to provide, improve, and administer our Services,
          communicate with you, for security and fraud prevention, and to comply with law.
          Specifically:
        </Body>
        <ul>
          <Bullet>To facilitate account creation and authentication and manage user accounts.</Bullet>
          <Bullet>To save or protect an individual's vital interest, such as to prevent harm.</Bullet>
        </ul>
      </Section>

      {/* 3 */}
      <Section id="legalbases" title="3. What Legal Bases Do We Rely On?">
        <Body>
          We only process your personal information when we have a valid legal reason. For
          users in the EU, UK, Switzerland, and Canada, we rely on:
        </Body>
        <ul>
          <Bullet><strong>Consent</strong> — when you have given us explicit permission.</Bullet>
          <Bullet><strong>Legal obligations</strong> — to comply with applicable law.</Bullet>
          <Bullet><strong>Vital interests</strong> — to protect your safety or that of a third party.</Bullet>
        </ul>
        <Body>
          For users in Canada, we may also process your information based on implied consent
          or under specific statutory exceptions permitted by Canadian privacy law.
        </Body>
      </Section>

      {/* 4 */}
      <Section id="whoshare" title="4. When and With Whom Do We Share Your Information?">
        <Body>
          We may share your information in specific situations:
        </Body>
        <ul>
          <Bullet>
            <strong>Business transfers.</strong> We may share information in connection with
            any merger, sale, financing, or acquisition of our business.
          </Bullet>
          <Bullet>
            <strong>Service providers.</strong> We share data with Stripe (payments) and
            Google Firebase (authentication, hosting, database) under written agreements.
          </Bullet>
        </ul>
        <Body>
          We have not sold or shared personal information to third parties for commercial
          purposes and will not do so in the future.
        </Body>
      </Section>

      {/* 5 */}
      <Section id="cookies" title="5. Do We Use Cookies and Other Tracking Technologies?">
        <Body>
          We may use cookies and similar tracking technologies to maintain the security of
          our Services, prevent crashes, fix bugs, save your preferences (such as dark mode),
          and assist with basic site functions. We use <strong>localStorage</strong> for
          theme preference and client-side rate limiting only.
        </Body>
        <Body>
          You can set your browser to remove or reject cookies, though this may affect
          certain features of the Services.
        </Body>
      </Section>

      {/* 6 */}
      <Section id="sociallogins" title="6. How Do We Handle Your Social Logins?">
        <Body>
          Our Services offer login via your Google account. When you choose to do this, we
          receive certain profile information from Google: your name, email address, and
          profile picture. We use this information only for account management purposes
          described in this notice.
        </Body>
        <Body>
          We are not responsible for other uses of your personal information by Google. We
          recommend reviewing{" "}
          <Link href="https://policies.google.com/privacy" target="_blank" color="primary">
            Google's Privacy Policy
          </Link>.
        </Body>
      </Section>

      {/* 7 */}
      <Section id="inforetain" title="7. How Long Do We Keep Your Information?">
        <Body>
          We keep your personal information only for as long as necessary to fulfil the
          purposes outlined in this notice, or as required by law. We retain your data for
          the period during which you maintain an account with us. Upon account deletion, we
          permanently delete your links and account data from our active databases.
        </Body>
      </Section>

      {/* 8 */}
      <Section id="infosafe" title="8. How Do We Keep Your Information Safe?">
        <Body>
          We have implemented appropriate technical and organisational security measures to
          protect your personal information, including HTTPS encryption (enforced by Firebase
          Hosting), Firestore Security Rules restricting data access to authorised users
          only, and Google's infrastructure-level security via Firebase.
        </Body>
        <Body>
          However, no electronic transmission over the Internet can be guaranteed 100%
          secure. You access the Services at your own risk and should only do so within a
          secure environment.
        </Body>
      </Section>

      {/* 9 */}
      <Section id="infominors" title="9. Do We Collect Information From Minors?">
        <Body>
          We do not knowingly collect data from or market to children under 18 years of age.
          By using the Services, you represent that you are at least 18. If we learn that
          personal information from a minor has been collected, we will deactivate the
          account and delete the data. Contact us at{" "}
          <Link href="mailto:imintcomx@gmail.com" color="primary">imintcomx@gmail.com</Link> if
          you become aware of any such data.
        </Body>
      </Section>

      {/* 10 */}
      <Section id="privacyrights" title="10. What Are Your Privacy Rights?">
        <Body>
          Depending on your location, you may have rights including: access to your
          personal data, rectification or erasure, restriction of processing, data
          portability, and the right to object to processing.
        </Body>
        <Body>
          EU/UK users may lodge a complaint with their{" "}
          <Link href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm" target="_blank" color="primary">
            Member State data protection authority
          </Link>{" "}or the{" "}
          <Link href="https://ico.org.uk/make-a-complaint/data-protection-complaints/" target="_blank" color="primary">
            UK ICO
          </Link>.
        </Body>
        <Body>
          <strong>Account deletion.</strong> You can permanently delete your account and all
          associated data at any time from the account menu in the app. Upon deletion, your
          links and account data are immediately and irreversibly removed.
        </Body>
        <Body>
          <strong>Withdrawing consent.</strong> You may withdraw consent at any time by
          contacting us. This does not affect the lawfulness of processing before withdrawal.
        </Body>
        <Body>
          For questions about your privacy rights, email{" "}
          <Link href="mailto:imintcomx@gmail.com" color="primary">imintcomx@gmail.com</Link>.
        </Body>
      </Section>

      {/* 11 */}
      <Section id="DNT" title="11. Controls for Do-Not-Track Features">
        <Body>
          Most web browsers include a Do-Not-Track ("DNT") feature. No uniform technology
          standard for recognising and implementing DNT signals has been finalised. We do not
          currently respond to DNT signals. If a standard is adopted in the future, we will
          update this notice accordingly.
        </Body>
        <Body>
          California law requires us to disclose how we respond to DNT signals. We do not
          respond to them at this time.
        </Body>
      </Section>

      {/* 12 */}
      <Section id="uslaws" title="12. Do United States Residents Have Specific Privacy Rights?">
        <Body>
          Residents of California, Colorado, Connecticut, and other US states with privacy
          legislation may have additional rights including: the right to know, access,
          correct, delete, and obtain a copy of your personal information; the right to
          non-discrimination; and the right to opt out of the sale of personal data.
        </Body>
        <Body>
          We do not sell personal data. The only category of personal information we collect
          is identifiers (email address via Google Sign-In) — retained for as long as your
          account exists.
        </Body>
        <Body>
          To exercise your rights, submit a{" "}
          <Link href="https://app.termly.io/dsar/b2149aba-eb08-4181-96eb-a953ce9a4285" target="_blank" color="primary">
            data subject access request
          </Link>{" "}or email{" "}
          <Link href="mailto:imintcomx@gmail.com" color="primary">imintcomx@gmail.com</Link>.
          Appeals may also be submitted to that email address.
        </Body>
        <Body>
          <strong>California "Shine The Light" Law.</strong> California residents may
          request information about personal data disclosed to third parties for direct
          marketing purposes. We do not share personal data for direct marketing.
        </Body>
      </Section>

      {/* 13 */}
      <Section id="policyupdates" title="13. Do We Make Updates to This Notice?">
        <Body>
          Yes, we may update this notice as necessary to stay compliant with relevant laws.
          The updated version will be indicated by an updated "Last updated" date at the top.
          We encourage you to review this notice periodically.
        </Body>
      </Section>

      {/* 14 */}
      <Section id="contact" title="14. How Can You Contact Us About This Notice?">
        <Body>
          If you have questions or comments about this notice, contact us at:
        </Body>
        <Body>
          <strong>BlinkzUS</strong><br />
          San Miguel de Allende, Guanajuato 37750<br />
          Mexico<br />
          <Link href="mailto:imintcomx@gmail.com" color="primary">imintcomx@gmail.com</Link>
        </Body>
      </Section>

      {/* 15 */}
      <Section id="request" title="15. How Can You Review, Update, or Delete Your Data?">
        <Body>
          Based on applicable law, you may request access to, correction of, or deletion of
          your personal information. The easiest way to delete your data is directly in the
          app via <strong>Account menu → Delete account</strong>, which permanently and
          immediately removes all your links and account data.
        </Body>
        <Body>
          You may also submit a formal{" "}
          <Link href="https://app.termly.io/dsar/b2149aba-eb08-4181-96eb-a953ce9a4285" target="_blank" color="primary">
            data subject access request
          </Link>.
        </Body>
      </Section>

      <Divider sx={{ my: 4 }} />

      <Typography variant="caption" color="text.disabled">
        This Privacy Policy was created with the assistance of{" "}
        <Link href="https://termly.io" target="_blank" color="inherit" underline="hover">Termly</Link>.
      </Typography>
    </Box>
  );
}
